import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Select, Space, Typography, message, Spin, Popover } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStethoscope,
  faChartLine,
  faClockRotateLeft,
  faUser,
  faCalendarDays,
  faCommentDots,
  faEnvelope,
  faVial
} from '@fortawesome/free-solid-svg-icons';
import PatientService from '../../services/PatientService';
import VisitService from '../../services/VisitService';
import { useStore } from '../../hooks/storeContext';
import PatientEditView from '../PatientQueue/PatientEditView';
import EventSelectionModal from '../PatientQueue/PatientEditView/components/EventSelectionModal';
import MemberLabResultsTab from './components/MemberLabResultsTab';
import { getBPCategory } from '../../utils/medicalReadingsUtils';
import { EditTabsContainer, EditTabButton } from '../PatientQueue/PatientEditView/styled';
import { usePageHeader } from '../../layouts/AdminLayout';
import {
  PageContainer,
  HeaderContainer,
  HeaderLeft,
  HeaderRight,
  NewVisitButton,
  NoVisitContainer,
  ContentArea,
  RiskScoreBadge,
  RiskPopoverContent,
  RiskPopoverTitle,
  RiskPopoverScore,
  RiskPopoverLevel,
  RiskPopoverDescription,
  selectStyles,
  titleStyles,
  noVisitTitleStyles,
  noVisitTextStyles,
  memberVisitSelectCSS
} from './styled';

const { Title } = Typography;

/**
 * The member record (visit picker + record tabs). `embedded` renders it inside
 * a drawer: no breadcrumb/URL syncing and no New Visit. `extraTabs`
 * ({ key, icon, label, render }) are shown first and work even when the
 * signed-in user can't open the medical record itself.
 */
export const MemberRecordContent = ({ patientId, embedded = false, extraTabs = [], defaultTabKey = '1' }) => {
  const { authStore } = useStore();
  const canMessage = authStore.isAdmin || authStore.hasPermission('messaging');
  const canEmail = authStore.isAdmin || authStore.hasPermission('case_management');
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageHeader } = usePageHeader();
  const [patientWrapper, setPatientWrapper] = useState(null);
  const [fullPatient, setFullPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recordError, setRecordError] = useState(null);
  const [activeVisitId, setActiveVisitId] = useState(embedded ? null : searchParams.get('visitId') || null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTabKey, setActiveTabKey] = useState(defaultTabKey); // 1: Medical Testing, 5: Visit History, 4: Trends, 3: Member Info
  const [recordTabKey, setRecordTabKey] = useState('1');
  const [eventModalVisible, setEventModalVisible] = useState(false);
  const [dbVisits, setDbVisits] = useState([]); // Visits from Visit collection (fallback)
  // Self-rendered tabs (not part of the visit record): caller's extras first, Lab Results last.
  const resultsTab = patientWrapper ? [{
    key: 'results',
    icon: <FontAwesomeIcon icon={faVial} />,
    label: 'Lab Results',
    render: () => <MemberLabResultsTab patientId={patientWrapper.patientId} />
  }] : [];
  const selfTabs = [...extraTabs, ...resultsTab];
  const extraTab = selfTabs.find((t) => t.key === activeTabKey);
  // Don't mount the (heavy) record view until someone opens one of its tabs.
  const [recordVisited, setRecordVisited] = useState(() => !extraTabs.some((t) => t.key === defaultTabKey));

  const selectTab = (key) => {
    setActiveTabKey(key);
    if (!selfTabs.some((t) => t.key === key)) {
      setRecordTabKey(key);
      setRecordVisited(true);
    }
  };
  const handleRecordTabChange = useCallback((key) => {
    setActiveTabKey(key);
    setRecordTabKey(key);
  }, []);
  // The page shows the record chrome while loading; the drawer waits for data.
  const showRecordChrome = !!patientWrapper || !embedded;

  // Set custom breadcrumb label for patient name in top nav
  useEffect(() => {
    if (embedded) return;
    const patientName = fullPatient 
      ? `${fullPatient.firstName || ''} ${fullPatient.lastName || ''}`.trim() || 'Member Details'
      : 'Loading...';
    
    setPageHeader({
      customBreadcrumbLabel: patientName
    });
  }, [fullPatient, setPageHeader, embedded]);

  const loadPatient = async () => {
    if (!patientId) return;
    setLoading(true);
    setRecordError(null);
    try {
      const res = await PatientService.getPatientDetails(patientId);
      if (!res?.data) {
        throw new Error(res?.message || 'No patient data returned');
      }
      setFullPatient(res.data);
      setPatientWrapper({ patient: res.data, patientId: res.data.patientId });

      const readings = res.data.screeningData?.medicalReadings || [];

      // If no medical readings yet, fetch visits from the Visit collection
      // so the user can still select and work on the visit
      if (readings.length === 0 && res.data.patientId) {
        const visits = await VisitService.getVisitsByPatientId(res.data.patientId);
        setDbVisits(visits);
        if (!activeVisitId && visits.length) {
          setActiveVisitId(visits[0].visitId || visits[0]._id);
        }
      } else {
        setDbVisits([]);
        // Auto-select latest reading-based visit if none chosen
        if (!activeVisitId && readings.length) {
          const sorted = [...readings].sort((a,b)=> new Date(b.visitDate)-new Date(a.visitDate));
          setActiveVisitId(sorted[0].visitId || sorted[0]._id);
        }
      }
    } catch (e) {
      console.error('Failed to load patient', e);
      const forbidden = /permission|access/i.test(e?.message || '');
      setRecordError(forbidden
        ? 'You don\'t have access to medical records (Screening Data permission).'
        : 'Unable to load patient record');
      if (!extraTabs.length) message.error('Unable to load patient record');
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=> { loadPatient(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [patientId, refreshKey]);

  const visitOptions = useMemo(()=> {
    const readings = fullPatient?.screeningData?.medicalReadings || [];

    // Use medical readings if available, otherwise fall back to Visit collection data
    const source = readings.length > 0 ? readings : dbVisits;

    return source
      .slice()
      .sort((a,b)=> new Date(b.visitDate)-new Date(a.visitDate))
      .map(v => {
        const latestVital = v.vitalSigns?.[v.vitalSigns.length-1];
        const bpCat = latestVital ? getBPCategory(latestVital.systolicBP, latestVital.diastolicBP) : null;
        const statusLabel = v.status ? ` (${v.status.replace(/_/g, ' ')})` : '';
        return {
          label: `${new Date(v.visitDate).toLocaleDateString()}${v.eventName ? ' • '+v.eventName : ''}${bpCat ? ' • ' + bpCat.text : ''}${!readings.length ? statusLabel : ''}`,
          value: v.visitId || v._id
        };
      });
  }, [fullPatient, dbVisits]);

  const handleVisitChange = (val) => {
    setActiveVisitId(val || null);
    if (embedded) return;
    if (val) {
      searchParams.set('visitId', val);
    } else {
      searchParams.delete('visitId');
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleAddNewVisit = () => {
    setEventModalVisible(true);
  };

  const handleEventSelected = (event) => {
    const newId = `visit_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
    setActiveVisitId(newId);
    searchParams.set('visitId', newId);
    searchParams.set('eventId', event._id);
    searchParams.set('eventName', event.title);
    searchParams.set('eventLocation', event.location || '');
    setSearchParams(searchParams, { replace: true });
    setEventModalVisible(false);
    message.success(`New visit started for ${event.title} – fill in readings and save to persist.`);
  };

  const handleSaveSuccess = useCallback(() => {
    message.success('Member record updated');
    setRefreshKey(k => k+1);
  }, []);

  // Memoize the patient prop to prevent creating a new object on every render,
  // which would cause PatientEditView's initialization useEffect to re-run unnecessarily (e.g. on tab switch).
  const patientProp = useMemo(() => {
    const readings = fullPatient?.screeningData?.medicalReadings || [];
    const activeVisit = readings.find(v => (v.visitId || v._id) === activeVisitId)
      || dbVisits.find(v => (v.visitId || v._id) === activeVisitId);
    const eventInfo = activeVisit ? {
      eventTitle: activeVisit.eventName || '',
      eventLocation: activeVisit.location || '',
      eventId: activeVisit.eventId
    } : {};
    return {
      ...patientWrapper,
      visitId: activeVisitId,
      eventId: activeVisit?.eventId || null,
      eventInfo
    };
  }, [patientWrapper, activeVisitId, fullPatient, dbVisits]);

  // Calculate if we have enough visits for trends (need at least 2)
  const hasEnoughVisitsForTrends = useMemo(() => {
    const visits = fullPatient?.screeningData?.medicalReadings || [];
    return visits.length >= 2;
  }, [fullPatient]);

  // Get risk score from latest visit
  const latestRiskScore = useMemo(() => {
    // Patient-level risk is authoritative (scored from the latest visit with testing data)
    if (fullPatient?.riskLevel === 'unknown') return null;
    if (fullPatient?.riskLevel && typeof fullPatient?.normalizedRiskScore === 'number') {
      return { score: fullPatient.normalizedRiskScore, level: fullPatient.riskLevel };
    }
    const visits = fullPatient?.screeningData?.medicalReadings || [];
    if (visits.length === 0) return null;
    const sorted = [...visits].sort((a,b) => new Date(b.visitDate) - new Date(a.visitDate));
    const latest = sorted[0];
    if (latest?.normalizedRiskScore !== undefined && latest?.normalizedRiskScore !== null) {
      return { score: latest.normalizedRiskScore, level: latest.riskLevel || 'unknown' };
    }
    return null;
  }, [fullPatient]);

  const recordTabs = activeVisitId && showRecordChrome ? [
    { key: '1', icon: <FontAwesomeIcon icon={faStethoscope} />, label: 'Medical Testing' },
    ...(hasEnoughVisitsForTrends ? [{ key: '4', icon: <FontAwesomeIcon icon={faChartLine} />, label: 'Trends' }] : []),
    { key: '5', icon: <FontAwesomeIcon icon={faClockRotateLeft} />, label: 'Visit History' },
    { key: '3', icon: <FontAwesomeIcon icon={faUser} />, label: 'Member Information' },
    { key: '6', icon: <FontAwesomeIcon icon={faCalendarDays} />, label: 'Event' },
    ...(canMessage ? [{ key: '7', icon: <FontAwesomeIcon icon={faCommentDots} />, label: 'Messaging' }] : []),
    ...(canEmail ? [{ key: '8', icon: <FontAwesomeIcon icon={faEnvelope} />, label: 'Email' }] : [])
  ] : [];
  const tabs = [...extraTabs, ...recordTabs, ...resultsTab];

  return (
    <PageContainer style={embedded ? { padding: 0 } : undefined}>
      <ContentArea>
      <Space direction="vertical" style={{ width: '100%', gap: 8 }}>
        {/* Header with Title and Risk Score */}
        <HeaderContainer>
          <HeaderLeft>
            {!embedded && (
            <Title level={3} style={titleStyles}>
              {fullPatient?.firstName || fullPatient?.lastName ? `${fullPatient.firstName || ''} ${fullPatient.lastName || ''}` : 'Member Record'}
            </Title>
            )}
            {latestRiskScore && !embedded && (
              <Popover
                content={
                  <RiskPopoverContent>
                    <RiskPopoverTitle>Cardiovascular Risk Assessment</RiskPopoverTitle>
                    <RiskPopoverScore level={latestRiskScore.level}>
                      {latestRiskScore.score}<span>/100</span>
                    </RiskPopoverScore>
                    <RiskPopoverLevel level={latestRiskScore.level}>
                      {latestRiskScore.level.charAt(0).toUpperCase() + latestRiskScore.level.slice(1)} Risk
                    </RiskPopoverLevel>
                    <RiskPopoverDescription>
                      {latestRiskScore.level === 'critical' && 'Immediate medical attention recommended. Multiple high-risk factors detected.'}
                      {latestRiskScore.level === 'high' && 'Significant risk factors present. Follow-up with healthcare provider advised.'}
                      {latestRiskScore.level === 'medium' && 'Some risk factors identified. Lifestyle modifications may be beneficial.'}
                      {latestRiskScore.level === 'low' && 'Minor risk factors. Continue healthy habits and regular check-ups.'}
                      {latestRiskScore.level === 'minimal' && 'Excellent cardiovascular health indicators. Maintain current lifestyle.'}
                    </RiskPopoverDescription>
                  </RiskPopoverContent>
                }
                trigger="hover"
                placement="bottom"
              >
                <RiskScoreBadge level={latestRiskScore.level}>
                  {latestRiskScore.score}
                </RiskScoreBadge>
              </Popover>
            )}
          </HeaderLeft>
          <style>{memberVisitSelectCSS}</style>
          {showRecordChrome && (
          <HeaderRight>
            <Select
              className="member-visit-select"
              style={selectStyles}
              placeholder="Select Visit"
              size="large"
              value={activeVisitId || undefined}
              onChange={handleVisitChange}
              options={visitOptions}
              dropdownMatchSelectWidth={400}
              allowClear
            />
            {!embedded && (
            <NewVisitButton 
              icon={<PlusOutlined />} 
              onClick={handleAddNewVisit}
            >
              New Visit
            </NewVisitButton>
            )}
          </HeaderRight>
          )}
        </HeaderContainer>

        {/* Top Navigation Tabs */}
        {tabs.length > 0 && (
          <EditTabsContainer>
            {tabs.map(t => (
              <EditTabButton
                key={t.key}
                $active={activeTabKey === t.key}
                onClick={() => selectTab(t.key)}
              >
                {t.icon} {t.label}
              </EditTabButton>
            ))}
          </EditTabsContainer>
        )}

        {/* Content Area */}
        {extraTab && <div style={{ paddingTop: 12 }}>{extraTab.render()}</div>}
        {!extraTab && recordError && extraTabs.length > 0 && (
          <NoVisitContainer>
            <p style={noVisitTextStyles}>{recordError}</p>
          </NoVisitContainer>
        )}
        {loading && !extraTab && <Spin />}
        {!loading && patientWrapper && activeVisitId && recordVisited && (
          <div style={extraTab ? { display: 'none' } : undefined}>
            <PatientEditView
              key={activeVisitId + '_' + refreshKey}
              initialPatientData={fullPatient}
              patient={patientProp}
              onSuccess={handleSaveSuccess}
              showCompleteButton={false}
              editVisitId={activeVisitId}
              suppressHeader
              forcedActiveTab={recordTabKey}
              onActiveTabChange={handleRecordTabChange}
              hideTabs
              recordsMode
            />
          </div>
        )}
        {!activeVisitId && !loading && !extraTab && showRecordChrome && (
          <NoVisitContainer>
            <Title level={4} style={noVisitTitleStyles}>No Visit Selected</Title>
            <p style={noVisitTextStyles}>
              {embedded
                ? 'Select a visit above to see their clinical data.'
                : 'Select a visit above or create a new one to begin editing clinical data. Member demographic information is loaded with each visit.'}
            </p>
            {!embedded && <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNewVisit}>Start New Visit</Button>}
          </NoVisitContainer>
        )}
      </Space>
      </ContentArea>
      
      {/* Event Selection Modal for New Visits */}
      {!embedded && (
      <EventSelectionModal
        visible={eventModalVisible}
        onCancel={() => setEventModalVisible(false)}
        onSelect={handleEventSelected}
        patientName={fullPatient ? `${fullPatient.firstName || ''} ${fullPatient.lastName || ''}`.trim() : 'this member'}
      />
      )}
    </PageContainer>
  );
};

const MemberRecordEditPage = () => {
  const { patientId } = useParams();
  return <MemberRecordContent patientId={patientId} />;
};

export default MemberRecordEditPage;
